/**
 * LaunchNotificationContext - Manages instance launch notifications globally.
 *
 * Shows a persistent notification when an instance is launching, with:
 * - Progress indicator
 * - Friendly status messages
 * - "Open Notebook" button when ready
 * - Auto-polling for instance status
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiX, FiExternalLink, FiLoader } from 'react-icons/fi';
import { getApiUrl } from '../utils/api';
import { getInstanceDisplayState } from '../utils/instanceStatus';

interface LaunchingInstance {
  id: string;
  instanceId: string;
  provider: string;
  instanceType: string;
  gpuType?: string;
  notebookPath?: string;
  status: 'launching' | 'configuring' | 'ready' | 'failed';
  message: string;
  startTime: number;
  jupyterUrl?: string;
  // Optional long-form copy + CTA shown in the toast during in-progress states.
  // Used by first-launch onboarding to explain the ~2-min cold start and nudge
  // the user toward the research feed while they wait.
  explainer?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

interface LaunchNotificationContextType {
  addLaunch: (launch: Omit<LaunchingInstance, 'id' | 'status' | 'message' | 'startTime'>) => string;
  removeLaunch: (id: string) => void;
  launches: LaunchingInstance[];
}

const LaunchNotificationContext = createContext<LaunchNotificationContextType | null>(null);

export function useLaunchNotification() {
  const context = useContext(LaunchNotificationContext);
  // Return a no-op version if not within provider (for safety)
  if (!context) {
    return {
      addLaunch: () => '',
      removeLaunch: () => {},
      launches: [],
    };
  }
  return context;
}

const STATUS_MESSAGES = {
  launching: [
    'Starting your GPU instance...',
    'Provisioning compute resources...',
    'Spinning up your workspace...',
  ],
  configuring: [
    'Installing dependencies...',
    'Setting up Jupyter environment...',
    'Almost ready...',
  ],
  ready: ['Your notebook is ready!'],
  failed: ['Something went wrong. Please try again.'],
};

function getRandomMessage(status: LaunchingInstance['status']): string {
  const messages = STATUS_MESSAGES[status];
  return messages[Math.floor(Math.random() * messages.length)];
}

function getElapsedTime(startTime: number): string {
  const elapsed = Math.floor((Date.now() - startTime) / 1000);
  if (elapsed < 60) return `${elapsed}s`;
  return `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
}

export function LaunchNotificationProvider({ children }: { children: React.ReactNode }) {
  const [launches, setLaunches] = useState<LaunchingInstance[]>([]);
  const pollingRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const removeLaunch = useCallback((id: string) => {
    // Stop polling
    const interval = pollingRefs.current.get(id);
    if (interval) {
      clearInterval(interval);
      pollingRefs.current.delete(id);
    }

    setLaunches(prev => prev.filter(l => l.id !== id));
  }, []);

  const updateLaunch = useCallback((id: string, updates: Partial<LaunchingInstance>) => {
    setLaunches(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
  }, []);

  // startPolling defined before addLaunch which uses it
  const startPolling = useCallback((launchId: string, instanceId: string) => {
    let pollCount = 0;
    const maxPolls = 120; // 10 minutes max (5s intervals)

    const poll = async () => {
      pollCount++;

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${getApiUrl()}/api/compute/instances/${instanceId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const instance = await response.json();

          if (instance) {
            let displayState = getInstanceDisplayState(instance.status, instance.terminal_ready);

            // When running but terminal_ready isn't confirmed yet, actively
            // trigger the readiness check (same as sidebar does) so the toast
            // doesn't lag behind waiting for the sidebar to persist it.
            if (displayState === 'starting') {
              try {
                const termResp = await fetch(
                  `${getApiUrl()}/api/compute/instances/${instanceId}/terminal/available`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (termResp.ok) {
                  const termData = await termResp.json();
                  if (termData.terminal_ready === true || termData.available === true) {
                    displayState = 'running';
                  }
                }
              } catch {
                // Not ready yet — will retry next poll
              }
            }

            if (displayState === 'running') {
              updateLaunch(launchId, {
                status: 'ready',
                message: 'Your notebook is ready!',
                jupyterUrl: `/dashboard/notebook/${instanceId}`,
              });
              const interval = pollingRefs.current.get(launchId);
              if (interval) { clearInterval(interval); pollingRefs.current.delete(launchId); }
            } else if (displayState === 'starting') {
              updateLaunch(launchId, {
                status: 'configuring',
                message: getRandomMessage('configuring'),
              });
            } else if (displayState === 'launching') {
              if (pollCount % 4 === 0) {
                updateLaunch(launchId, { message: getRandomMessage('launching') });
              }
            } else if (displayState === 'error' || displayState === 'terminated') {
              updateLaunch(launchId, {
                status: 'failed',
                message: instance.error_message || 'Instance failed to launch',
              });
              const interval = pollingRefs.current.get(launchId);
              if (interval) { clearInterval(interval); pollingRefs.current.delete(launchId); }
            }
          }
        }
      } catch (err) {
        console.debug('Error polling instance status:', err);
      }

      // Timeout after max polls
      if (pollCount >= maxPolls) {
        updateLaunch(launchId, {
          status: 'failed',
          message: 'Launch timed out. Please check the Compute page.',
        });
        const interval = pollingRefs.current.get(launchId);
        if (interval) { clearInterval(interval); pollingRefs.current.delete(launchId); }
      }
    };

    // Poll immediately, then every 5 seconds
    poll();
    const interval = setInterval(poll, 5000);
    pollingRefs.current.set(launchId, interval);
  }, [updateLaunch]);

  // addLaunch defined after startPolling which it depends on
  const addLaunch = useCallback((launch: Omit<LaunchingInstance, 'id' | 'status' | 'message' | 'startTime'>) => {
    const id = `launch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newLaunch: LaunchingInstance = {
      ...launch,
      id,
      status: 'launching',
      message: getRandomMessage('launching'),
      startTime: Date.now(),
    };

    setLaunches(prev => [...prev, newLaunch]);

    // Start polling for status
    startPolling(id, launch.instanceId);

    return id;
  }, [startPolling]);

  // Cleanup on unmount
  useEffect(() => {
    const refs = pollingRefs.current;
    return () => {
      refs.forEach(interval => clearInterval(interval));
      refs.clear();
    };
  }, []);

  return (
    <LaunchNotificationContext.Provider value={{ addLaunch, removeLaunch, launches }}>
      {children}
      <LaunchNotifications launches={launches} onDismiss={removeLaunch} />
    </LaunchNotificationContext.Provider>
  );
}

interface LaunchNotificationsProps {
  launches: LaunchingInstance[];
  onDismiss: (id: string) => void;
}

function LaunchNotifications({ launches, onDismiss }: LaunchNotificationsProps) {
  if (launches.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-3 max-w-sm">
      {launches.map(launch => (
        <LaunchNotificationCard key={launch.id} launch={launch} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

interface LaunchNotificationCardProps {
  launch: LaunchingInstance;
  onDismiss: (id: string) => void;
}

function LaunchNotificationCard({ launch, onDismiss }: LaunchNotificationCardProps) {
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState(getElapsedTime(launch.startTime));

  // Update elapsed time every second for in-progress launches
  useEffect(() => {
    if (launch.status === 'launching' || launch.status === 'configuring') {
      const interval = setInterval(() => {
        setElapsed(getElapsedTime(launch.startTime));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [launch.status, launch.startTime]);

  const isInProgress = launch.status === 'launching' || launch.status === 'configuring';
  const isReady = launch.status === 'ready';
  const isFailed = launch.status === 'failed';

  const handleOpenNotebook = () => {
    const notebookPath = launch.notebookPath
      || sessionStorage.getItem(`pending_notebook_for_${launch.instanceId}`)
      || sessionStorage.getItem('pending_sample_notebook');
    sessionStorage.setItem('notebook_launch_params', JSON.stringify({
      instance_id: launch.instanceId,
      ...(notebookPath && { notebook_path: notebookPath }),
    }));
    // Don't remove pending_notebook_for_* here — let autoOpenNotebook
    // clean up after successfully loading cells. If the WebSocket or
    // Jupyter init times out, the key survives for the retry attempt.
    onDismiss(launch.id);
    navigate('/dashboard/notebook/new-session');
  };

  return (
    <div
      className={`bg-gray-900 border rounded-lg shadow-xl overflow-hidden transition-all animate-slide-in ${
        isReady ? 'border-green-500/50' : isFailed ? 'border-red-500/50' : 'border-gray-700'
      }`}
    >
      {/* Progress bar for in-progress launches */}
      {isInProgress && (
        <div className="h-1 bg-gray-800">
          <div className="h-full bg-gradient-to-r from-blue-500 to-green-500 animate-progress-indeterminate" />
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isReady ? 'bg-green-500/20' : isFailed ? 'bg-red-500/20' : 'bg-blue-500/20'
          }`}>
            {isInProgress ? (
              <FiLoader className="w-5 h-5 text-blue-400 animate-spin" />
            ) : isReady ? (
              <FiCheck className="w-5 h-5 text-green-400" />
            ) : (
              <FiX className="w-5 h-5 text-red-400" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h4 className="font-medium text-white truncate">
                {launch.gpuType || launch.instanceType}
              </h4>
              <button
                onClick={() => onDismiss(launch.id)}
                className="p-1 text-gray-500 hover:text-white transition-colors flex-shrink-0"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            <p className={`text-sm mt-0.5 ${
              isReady ? 'text-green-400' : isFailed ? 'text-red-400' : 'text-gray-400'
            }`}>
              {launch.message}
            </p>

            {/* Provider and time */}
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
              <span className={`px-1.5 py-0.5 rounded ${
                launch.provider === 'aws' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
              }`}>
                {launch.provider.toUpperCase()}
              </span>
              {isInProgress && <span>{elapsed}</span>}
            </div>

            {/* First-launch explainer + CTA (shown only while in progress, if provided). */}
            {isInProgress && launch.explainer && (
              <p className="mt-2 text-xs text-gray-400 leading-relaxed">
                {launch.explainer}
              </p>
            )}
            {isInProgress && launch.ctaHref && launch.ctaLabel && (
              <a
                href={launch.ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                {launch.ctaLabel} →
              </a>
            )}

            {/* Action button when ready */}
            {isReady && (
              <button
                onClick={handleOpenNotebook}
                className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
              >
                <FiExternalLink className="w-4 h-4" />
                Open Notebook
              </button>
            )}

            {/* Retry hint when failed */}
            {isFailed && (
              <p className="mt-2 text-xs text-gray-500">
                Check the <a href="/dashboard/compute" className="text-blue-400 hover:underline">Compute page</a> for details.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
